import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Form } from "react-bootstrap";
import { colors } from "utils";
import { useStandards } from "hooks/useStandards/useStandards";
import { useSemanticStandardSearch } from "hooks/useSemanticStandardSearch";
import { getGradeLabel } from "utils/standards";
import { SearchBar } from "components/shared/SearchBar/SearchBar";
import { ViewQuestionsModal } from "components/pages/Educator/Dashboard/ViewQuestionsModal";
import { AccordionSection, StandardRadioRow } from "./StandardPicker";

// Standards picked from the library are assigned via the "other" track — the
// backend escape hatch that accepts any existing standard, unlike the lesson's
// core / prerequisite / extension tracks which stay constrained to its
// alignments. Mirrors the differentiation flow's EditGroupFocus library panel.
const LIBRARY_TRACK = "other";

// Hide standards/clusters/domains that have no usable questions (matches the
// tree visibility rules in StandardNavigationTree / EditGroupFocus).
const stdVisible = (s) => s.count !== 0 && s.challenge_count !== 0;
const clusterVisible = (c) => (c.standards || []).some(stdVisible);
const domainVisible = (d) => (d.clusters || []).some(clusterVisible);
const gradeKeyOf = (code) => (code ? (code.startsWith("HS") ? "HS" : code.split(".")[0]) : "");

const SearchPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 16px 8px;
`;

const StyledSearchBar = styled(SearchBar)`
  border: 1px solid ${colors.blue300};
  flex: none;
  width: 100%;
`;

const GradeField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 220px;
`;

const GradeLabel = styled.span`
  color: ${colors.nightlyWoods500};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

const BrowseHeader = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrowseLabel = styled.span`
  color: #f7622b;
  font-family: "ABC Marfa", sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 18px;
  text-transform: uppercase;
`;

const BrowseName = styled.span`
  color: ${colors.nightlyWoods500};
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
`;

const CountPill = styled.span`
  align-self: center;
  background-color: ${colors.nightlyWoods500};
  border-radius: 9999px;
  color: white;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  padding: 2px 10px;
  white-space: nowrap;
`;

const EmptyResults = styled.p`
  color: ${colors.milkGlass900};
  font-size: 14px;
  font-style: italic;
  line-height: 20px;
  margin: 8px 0;
`;

const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

// Self-contained "Search or Browse Standards Library" section. Owns its search,
// grade filter, and View Questions modal; reports the chosen standard up via
// onSelect (always tagged with the "other" track). `selected` drives the radio
// checked state. `primaryCode` seeds the default grade filter.
export const StandardLibrarySection = ({
  primaryCode,
  selected,
  onSelect,
  title = "Search or Browse Standards Library",
  // Open on load when the group's current focus lives here (an "other" standard).
  defaultExpanded = false,
}) => {
  const standardsData = useStandards(true);
  const {
    query, results, isSearching, showResults,
    handleInputChange, handleSubmit, inputRef,
  } = useSemanticStandardSearch();
  const [grade, setGrade] = useState("");
  const [viewQuestionsTarget, setViewQuestionsTarget] = useState(null);

  const availableGrades = useMemo(
    () => (standardsData || []).filter((g) => (g.domains || []).some(domainVisible)).map((g) => g.grade),
    [standardsData],
  );
  const primaryGrade = gradeKeyOf(primaryCode);

  // Default the grade filter to the Core standard's grade. Wait for primaryGrade
  // (from the bundle fetch) as well as the standards list — otherwise, if
  // standards load first, we'd default to availableGrades[0] and the `if (grade)`
  // guard would then block the correction once the Core standard arrives.
  useEffect(() => {
    if (grade || availableGrades.length === 0 || !primaryGrade) return;
    setGrade(availableGrades.includes(primaryGrade) ? primaryGrade : availableGrades[0]);
  }, [grade, availableGrades, primaryGrade]);

  const gradeDomains = useMemo(() => {
    const g = (standardsData || []).find((x) => x.grade === grade);
    return (g?.domains || []).filter(domainVisible);
  }, [standardsData, grade]);

  // Only standards (not domains/clusters) are assignable from search results.
  const searchStandards = useMemo(
    () => (results || []).filter((r) => r.entity_type === "standard"),
    [results],
  );

  const renderRow = (option, pl) => (
    <StandardRadioRow
      key={`${option.trackType}:${option.code}`}
      option={option}
      pl={pl}
      checked={selected?.code === option.code && selected?.trackType === option.trackType}
      onSelect={onSelect}
      onViewQuestions={(s) => setViewQuestionsTarget({ code: s.code, name: s.name })}
    />
  );

  const toOption = (s) => ({ code: s.code, name: s.name, description: s.description, trackType: LIBRARY_TRACK });

  return (
    <>
      <AccordionSection title={title} defaultExpanded={defaultExpanded}>
        <SearchPanel>
          <StyledSearchBar
            ref={inputRef}
            value={query}
            onChange={handleInputChange}
            onSubmit={handleSubmit}
            placeholder={'Try: "Multiplying Fractions" or "Proportional relationships"'}
            ariaLabel="Search or browse standards library"
            isLoading={isSearching}
          />
          {!showResults && (
            <GradeField>
              <GradeLabel as="label" htmlFor="lesson-egf-grade-select">Grade</GradeLabel>
              <Form.Select id="lesson-egf-grade-select" value={grade} onChange={(e) => setGrade(e.target.value)}>
                {availableGrades.map((g) => (
                  <option key={g} value={g}>{getGradeLabel(g)}</option>
                ))}
              </Form.Select>
            </GradeField>
          )}
        </SearchPanel>

        {showResults ? (
          <>
            <SrOnly role="status" aria-live="polite">
              {searchStandards.length > 0
                ? `${searchStandards.length} standard${searchStandards.length === 1 ? "" : "s"} found for ${query}`
                : `No standards found for ${query}`}
            </SrOnly>
            {searchStandards.length > 0
              ? searchStandards.map((s) => renderRow(toOption(s), 16))
              : <EmptyResults>No standards found for &ldquo;{query}&rdquo;.</EmptyResults>}
          </>
        ) : (
          gradeDomains.map((domain) => {
            const count = (domain.clusters || []).reduce(
              (n, c) => n + (c.standards || []).filter(stdVisible).length, 0);
            return (
              <AccordionSection
                key={domain.code}
                level={1}
                header={(
                  <BrowseHeader>
                    <BrowseLabel>Domain - {domain.code}</BrowseLabel>
                    <BrowseName>{domain.name}</BrowseName>
                  </BrowseHeader>
                )}
                right={<CountPill>{count} {count === 1 ? "Standard" : "Standards"}</CountPill>}
              >
                {(domain.clusters || []).filter(clusterVisible).map((cluster) => (
                  <AccordionSection
                    key={cluster.code}
                    level={2}
                    header={(
                      <BrowseHeader>
                        <BrowseLabel>Cluster - {cluster.code}</BrowseLabel>
                        <BrowseName>{cluster.name}</BrowseName>
                      </BrowseHeader>
                    )}
                  >
                    {(cluster.standards || []).filter(stdVisible).map((s) => renderRow(toOption(s), 84))}
                  </AccordionSection>
                ))}
              </AccordionSection>
            );
          })
        )}
      </AccordionSection>

      <ViewQuestionsModal
        isOpen={Boolean(viewQuestionsTarget)}
        onClose={() => setViewQuestionsTarget(null)}
        standardCode={viewQuestionsTarget?.code}
        standardName={viewQuestionsTarget?.name}
      />
    </>
  );
};
